<template>
  <div class="container-fluid">
    <div class="card card-default custom-card-height">
      <div class="card-header feature-header">
        <div class="row g-2">
          <div class="col-auto me-auto">
            <CardTitleComponent />
          </div>
          <div class=col-auto>
            <div class="search-container">
              <span class="search-icon" style="cursor: pointer;" @click="onSearch">
                <i class="fas fa-search"></i>
              </span>
              <input type="text" :placeholder="$t('search')" class="search-input" v-model="searchText"
                @keyup.enter="onSearch" />
              <span v-if="searchText" class="clear-icon" @click="clearSearch">
                <i class="fas fa-times"></i>
              </span>
            </div>
          </div>

          <div class="col-auto">
            <a class="btn btn-outline-primary" @click="open(true)">
              <i class="fas fa-plus me-1"></i>{{ $t('addNew') }}
            </a>
          </div>

        </div>
      </div>

      <div class="card-body p-2">
        <div class="card-body-grid">
          <ejs-grid ref="gridModule" :dataSource="data" :allowPaging="true" :allowResizing='true'
            :pageSettings='pageSettings' :locale='locale' :rowHeight='38' :allowSorting='true'
            :searchSettings='searchOptions' :dataStateChange='dataStateChange'>
            <e-columns>
              <e-column :headerText="$t('name')" field='Name' width='100'></e-column>
              <e-column :headerText="$t('displayName')" field='DisplayName' width='100'></e-column>
              <e-column :headerText="$t('actions')" width='50' textAlign="Center" :template="'actionTemplate'">
                <template v-slot:actionTemplate="{ data }">
                  <div class="btn-group actions-row" role="group" aria-label="Basic example">
                    <a href="javascript:void(0)" class="ms-2 action-link action-edit" title="Edit"
                      @click="commandClick('Edit', data)">
                      <i class="e-edit e-icons" style="font-size: 0.76em;"></i>
                    </a>
                  </div>
                </template>
              </e-column>
            </e-columns>
          </ejs-grid>
        </div>
      </div>
      <!-- /.card-body -->
    </div>
  </div>
  <ConfirmComponent :title="confirmTitle" @yes="commandConfirm()" />
  <div class="modal fade" id="moduleModal" module="dialog">
    <div class="modal-dialog modal-md">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">{{ $t('module') }}</h4>
          <button type="button" class="close" data-bs-dismiss="modal" aria-label="Close" @click="close()">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <form @submit="onUpsert">
          <div class="modal-body">
            <div class="row">
              <div class="col">
                <div class="form-group">
                  <label>{{ $t('name') }} <span class="text-danger">*</span></label>
                  <input name="displayName" type="text" class="form-control input-sm rounded-0"
                    v-model="form.displayName" required />
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col">
                <div class="form-group mt-2 d-flex align-items-center gap-2">
                  <input type="checkbox" id="active" class="checkbox" v-model="form.isActive">
                  <label for="active" class="mb-0">{{ $t('active') }}</label>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-sm btn-outline-close" @click="close()">
              <i class="fas fa-times"></i>&nbsp;{{ $t('close') }}
            </button>
            <button type="submit" class="btn btn-sm btn-outline-info-reverse">
              <i class="fas fa-save"></i>&nbsp;{{ isUpdate ? $t('update') : $t('save') }}
            </button>
          </div>
        </form>
      </div>
      <!-- /.modal-content -->
    </div>
    <!-- /.modal-dialog -->
  </div>
</template>

<script lang="ts">
import './ModuleComponent.css';
import {
  GridComponent as EjsGrid,
  ColumnsDirective as EColumns,
  ColumnDirective as EColumn,
  Resize,
  Toolbar,
  Search,
  Page,
  Group,
  Sort,
} from '@syncfusion/ej2-vue-grids'
import { defineComponent } from 'vue'
import modal from '@/components/common/modalManage'

import ConfirmComponent from '@/components/molecules/ConfirmComponent.vue'
import agent, { masterServiceBaseURL } from '@/services/agent'
import toasterService from '@/services/toasterService'
import { L10n, setCulture } from '@syncfusion/ej2-base'
import {
  Module,
  ModuleGetResponse,
  ModuleResponse,
  Property,
} from './Module.model'
import authService from '@/services/authService'
import CardTitleComponent from '@/components/common/CardTitleComponent.vue';

let _moduleData: Property = {} as Property
let _moduleForm: Module = {} as Module

export default defineComponent({
  components: {
    'ejs-grid': EjsGrid,
    'e-columns': EColumns,
    'e-column': EColumn,
    ConfirmComponent,
    CardTitleComponent,
  },
  provide: {
    grid: [Toolbar, Resize, Search, Page, Sort, Group],
  },
  created() {
    agent.setBaseUrl(masterServiceBaseURL)
    this.gridLocalize()
  },
  data() {
    return {
      data: _moduleData,
      form: _moduleForm,
      isUpdate: false,
      isTableLoad: false,
      confirmTitle: '',
      confirmIsDelete: false,
      selectedState: null,
      searchOptions: {
        fields: ['Name', 'DisplayName'],
        operator: 'contains',
        ignoreCase: true,
      },
      toolbar: ['Search'],
      searchText: '',
      pageSettings: {
        pageSizes: [10, 20, 50, 100],
        pageSize: 50,
        pageCount: 5,
      },
      locale: '',
    }
  },
  mounted() {
    this.dataStateChange(this.initState())
  },
  methods: {
    initState(): any {
      let state = {
        skip: 0,
        take: this.pageSettings.pageSize,
      }
      return state
    },
    dataStateChange(state: any) {
      this.gridLocalize()
      this.loadModule(state)
    },
    async loadModule(state: any) {
      if (state.action) {
        delete state.action
      }

      const gridModule: any = this.$refs.gridModule
      if (gridModule) gridModule.hideSpinner()

      const response = await agent.post<ModuleGetResponse>(
        `v1/module/getmodule`,
        state
      )
      if (response && response.result) {
        this.data = response.result
      }
    },
    onUpsert(event: any) {
      event.preventDefault()

      if (this.isUpdate) {
        this.onEdit()
      } else {
        this.onSave()
      }
    },
    onSave() {
      let post = { ...this.form }
      post.name = this.form.displayName
      post.createdBy = authService.getUserEmail()

      agent
        .post<ModuleResponse>(`v1/module`, post)
        .then((response) => {
          if (response.result) {
            toasterService.success(this.$t('msgSave'))
            this.loadModule(this.initState())
            this.resetModule()
            this.close()
          }
        })
        .catch(() => { })
    },
    onEdit() {
      let put = { ...this.form }
      put.modifiedBy = authService.getUserEmail()

      agent
        .put<ModuleResponse>(`v1/module`, put)
        .then((response) => {
          if (response.result) {
            toasterService.success(this.$t('msgUpdate'))
            this.loadModule(this.initState())
            this.resetModule()
            this.close()
          }
        })
        .catch(() => { })
    },
    onDelete() {
      let put = { ...this.form }

      agent
        .delete<ModuleResponse>(`v1/module/${put.id}`)
        .then((response) => {
          if (response.result) {
            toasterService.success(this.$t('msgDelete'))
            this.loadModule(this.initState())
            this.resetModule()
          }
        })
        .catch(() => { })
    },
    resetModule() {
      this.form = {} as Module
      this.isUpdate = false
    },
    commandConfirm() {
      if (this.confirmIsDelete) {
        this.onDelete()
        this.confirmIsDelete = false
      }
    },
    commandClick: function (type: string, data: Module) {
      this.isUpdate = false
      switch (type) {
        case 'Edit':
          this.form = data
          this.isUpdate = true
          modal.Open('moduleModal')
          break
        default:
          break
      }
    },
    gridLocalize() {
      this.locale = 'en-grid'
      if (this.$i18n.locale == 'se') {
        setTimeout(() => {
          import(`@/assets/sv.json`).then((module) => {
            const localText = module.default
            this.locale = 'sv'
            setCulture('sv')
            L10n.load(localText)
          })
        })
      }
    },
    open() {
      this.form = {} as Module
      this.isUpdate = false
      modal.Open('moduleModal')
    },
    close() {
      this.form = {} as Module
      modal.Close('moduleModal')
    },
    onSearch() {
      const grid = (this.$refs.gridModule as any)?.ej2Instances
      if (grid) grid.search(this.searchText)
    },

    clearSearch() {
      this.searchText = ''
      const grid = (this.$refs.gridModule as any)?.ej2Instances
      if (grid) grid.search('')
    }
  },
})
</script>
